/**
 * NodeDetailPanel — right-side inspector for a selected workstream node.
 *
 * Renders the node name, current state, timing, and a read-only JSON
 * view of the input/output payloads. Logs are shown as a monospace
 * pre-block. Empty fields collapse to a one-line muted placeholder.
 */
import type { ArchitectNode } from '@chaos/types/architect';
import type * as React from 'react';

import { cn } from '../../lib/utils';

interface NodeDetailPanelProps {
  node: ArchitectNode | null;
}

function formatLabel(name: string): string {
  return name
    .split('_')
    .map((piece, i) => (i === 0 ? piece.charAt(0).toUpperCase() + piece.slice(1) : piece))
    .join(' ');
}

function formatDuration(start: string | null, end: string | null): string | null {
  if (!start) return null;
  const startMs = new Date(start).getTime();
  const endMs = end ? new Date(end).getTime() : Date.now();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null;
  const delta = Math.max(0, endMs - startMs);
  if (delta < 1_000) return `${delta}ms`;
  if (delta < 60_000) return `${(delta / 1_000).toFixed(1)}s`;
  return `${Math.floor(delta / 60_000)}m ${Math.floor((delta % 60_000) / 1_000)}s`;
}

function stateBadgeClass(state: ArchitectNode['state']): string {
  switch (state) {
    case 'done':
      return 'bg-success/15 text-success';
    case 'running':
      return 'bg-foreground/8 text-foreground';
    case 'failed':
      return 'bg-error/15 text-error';
    case 'skipped':
      return 'bg-warning/15 text-warning';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

function JsonView({ value }: { value: unknown }): React.JSX.Element {
  if (value === null || value === undefined) {
    return <span className="text-[12px] text-muted-foreground/70">empty</span>;
  }
  let text: string;
  try {
    text = JSON.stringify(value, null, 2);
  } catch {
    text = String(value);
  }
  return (
    <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words rounded bg-muted/40 px-3 py-2 text-[11px] leading-snug text-foreground/90">
      {text}
    </pre>
  );
}

export function NodeDetailPanel({ node }: NodeDetailPanelProps): React.JSX.Element {
  if (!node) {
    return (
      <div className="flex h-full items-center justify-center px-6 text-center text-[12px] text-muted-foreground/70">
        Select a node to inspect its input, output, and logs.
      </div>
    );
  }

  const duration = formatDuration(node.startedAt, node.endedAt);
  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-col gap-2 border-b border-border px-5 py-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-[14px] font-semibold tracking-tight text-foreground">
            {formatLabel(node.name)}
          </h3>
          <span
            className={cn(
              'rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider',
              stateBadgeClass(node.state),
            )}
          >
            {node.state}
          </span>
        </div>
        <dl className="grid grid-cols-2 gap-y-1 text-[11px] text-muted-foreground">
          <dt>Position</dt>
          <dd className="text-foreground">{node.position}</dd>
          <dt>Started</dt>
          <dd className="text-foreground">
            {node.startedAt ? new Date(node.startedAt).toLocaleTimeString() : '—'}
          </dd>
          <dt>Ended</dt>
          <dd className="text-foreground">
            {node.endedAt ? new Date(node.endedAt).toLocaleTimeString() : '—'}
          </dd>
          <dt>Duration</dt>
          <dd className="text-foreground">{duration ?? '—'}</dd>
        </dl>
      </header>

      <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
        <section>
          <h4 className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Input
          </h4>
          <JsonView value={node.inputJson} />
        </section>

        <section>
          <h4 className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Output
          </h4>
          <JsonView value={node.outputJson} />
        </section>

        <section>
          <h4 className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Logs
          </h4>
          {node.logs ? (
            <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words rounded bg-muted/40 px-3 py-2 font-mono text-[11px] leading-snug text-foreground/90">
              {node.logs}
            </pre>
          ) : (
            <span className="text-[12px] text-muted-foreground/70">empty</span>
          )}
        </section>
      </div>
    </div>
  );
}
